<template>
  <div>
    <form-section
      :title="$t('CampaignForm_Schedule_Optimove_Template_Settings_Title')"
      :description="$t('CampaignForm_Schedule_Optimove_Template_Settings_Desc')"
    >
      <el-row>
        <el-col class="date-time-ct">
          <form-item
            prop="end_date"
            :label="$t('CampaignForm_Schedule_Optimove_Date')"
            class="date-input input"
          >
            <el-date-picker
              format="MMMM d, yyyy"
              value-format="yyyy-MM-dd"
              v-model="model.end_date"
              :placeholder="
                $t('CampaignForm_Schedule_Optimove_Placeholder_ManualStop')
              "
            />
          </form-item>
          <form-item
            prop="end_date_time"
            :label="$t('CampaignForm_Schedule_Optimove_Time')"
            class="time-input input"
          >
            <el-time-picker
              format="HH:mm"
              value-format="HH:mm"
              v-model="model.end_date_time"
              :placeholder="
                $t('CampaignForm_Schedule_Optimove_Placeholder_StopTime')
              "
            />
          </form-item>
          <form-item
            prop="timezone"
            :label="$t('CampaignForm_Schedule_Optimove_Timezone')"
            class="timezone-select"
          >
            <el-select v-model="model.end_date_timezone" filterable clearable>
              <el-option
                v-for="(label, value) in timezonesFormatted"
                :key="value"
                :value="value"
                :label="label"
              >
                <span v-tooltip>{{ label }}</span>
              </el-option>
            </el-select>
          </form-item>
        </el-col>
      </el-row>
      <div class="separator"></div>
      <el-row>
        <el-col>
          <form-item
            v-if="optimoveIntegration"
            prop="optimove_type"
            :label="$t('CampaignForm_Schedule_Optimove_Channel_Label')"
          >
            <radio-group v-model="model.optimove_type" inline>
              <radio
                v-if="hasScheduledChannel"
                :border="true"
                :label="0"
                :title="
                  $t('CampaignForm_Schedule_Optimove_Channel_Xtremepush_Title')
                "
                :description="
                  $t('CampaignForm_Schedule_Optimove_Channel_Xtremepush_Desc')
                "
              />
              <radio
                v-if="hasScheduledWebChannel"
                :border="true"
                :label="1"
                :title="
                  $t('CampaignForm_Schedule_Optimove_Channel_Webpush_Title')
                "
                :description="
                  $t('CampaignForm_Schedule_Optimove_Channel_Webpush_Desc')
                "
              />
              <radio
                v-if="hasRealtimeChannel"
                :border="true"
                :label="2"
                :title="
                  $t('CampaignForm_Schedule_Optimove_Channel_Triggered_Title')
                "
                :description="
                  $t('CampaignForm_Schedule_Optimove_Channel_Triggered_Desc')
                "
              />
              <radio
                v-if="hasNewRealtimeChannel"
                :border="true"
                :label="3"
                :title="
                  $t('CampaignForm_Schedule_Optimove_Channel_Triggered_Title')
                "
                :description="
                  $t('CampaignForm_Schedule_Optimove_Channel_Triggered_Desc')
                "
              />
            </radio-group>
            <el-alert
              v-if="
                !hasRealtimeChannel &&
                  !hasRealtimeChannel &&
                  !hasScheduledChannel &&
                  !hasScheduledWebChannel
              "
              :title="
                $t('CampaignForm_Schedule_Optimove_Alert_SelectChannel_Title')
              "
              type="info"
              :description="
                $t('CampaignForm_Schedule_Optimove_Alert_SelectChannel_Desc')
              "
              show-icon
              :closable="false"
            >
            </el-alert>
          </form-item>
          <el-alert
            v-else
            :title="
              $t('CampaignForm_Schedule_Optimove_Alert_NoIntegration_Title')
            "
            type="info"
            :description="
              $t('CampaignForm_Schedule_Optimove_Alert_NoIntegration_Desc')
            "
            show-icon
            :closable="false"
          >
          </el-alert>
        </el-col>
      </el-row>
    </form-section>
    <offline-users-component
      v-if="model.ios_push || model.android_push || model.web_push"
      :model="model"
    />
    <persist-content-component v-if="model.inbox" :model="model" />
    <template v-if="model.type === campaignTypeEnum.TYPE_INAPP">
      <form-section
        :title="$t('CampaignForm_Schedule_Optimove_Message_Settings_Title')"
        :description="
          $t('CampaignForm_Schedule_Optimove_Message_Settings_Desc')
        "
      >
        <form-item
          :label="
            $t('CampaignForm_Schedule_Optimove_Message_Settings_Trigger_Days')
          "
          prop="send_days"
        >
          <checkbox-group
            v-model="model.send_days"
            :labels="dayOfWeekLabels"
            :border="true"
            :keys-is-int="true"
          />
        </form-item>
        <div class="separator"></div>
        <div class="title title-large m-top-22">
          {{
            $t(
              'CampaignForm_Schedule_Optimove_Message_Settings_Delivery_Window'
            )
          }}
        </div>
        <div class="m-top-10 date-time-ct">
          <form-item
            :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_From')"
            class="time-input light-label input"
            prop="start_time"
          >
            <el-time-picker
              format="HH:mm"
              value-format="HH:mm"
              v-model="model.start_time"
            />
          </form-item>
          <form-item
            :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_To')"
            class="time-input light-label"
            prop="end_time"
          >
            <el-time-picker
              format="HH:mm"
              value-format="HH:mm"
              v-model="model.end_time"
            />
          </form-item>
        </div>
      </form-section>
    </template>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import Radio from '@/common/components/form/Radio'
import RadioGroup from '@/common/components/form/RadioGroup'
import CheckboxGroup from '@/common/components/form/CheckboxGroup'
import OfflineUsersComponent from '@/modules/campaign-manage/components/schedule/OfflineUsersComponent'
import PersistContentComponent from '@/modules/campaign-manage/components/schedule/PersistContentComponent'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { mapGetters } from 'vuex'
import Integrations from '@/modules/integrations/types'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { dayOfWeekLabels } from '@/modules/campaign-manage/enum/Labels'

export default {
  name: 'ScheduleSelectorOptimove',
  components: {
    PersistContentComponent,
    OfflineUsersComponent,
    RadioGroup,
    CheckboxGroup,
    Radio,
    FormItem,
    FormSection
  },
  data() {
    return {
      dayOfWeekLabels,
      campaignTypeEnum,
      optimoveIntegration: null
    }
  },
  props: {
    model: {
      type: Object
    }
  },
  mounted() {
    RestApi(this.fetchEndpoint).then(({ data }) => {
      this.optimoveIntegration = data[0] !== undefined ? data[0] : null
    })
  },
  computed: {
    ...mapGetters(['timezones']),
    hasRealtimeChannel() {
      return (
        this.optimoveIntegration &&
        this.model.type === campaignTypeEnum.TYPE_NOTIFICATION &&
        !!this.optimoveIntegration.params.realtime_channel
      )
    },
    hasNewRealtimeChannel() {
      return (
        this.optimoveIntegration &&
        this.model.type === campaignTypeEnum.TYPE_NOTIFICATION &&
        !!this.optimoveIntegration.params.new_realtime_channel
      )
    },
    hasScheduledChannel() {
      return (
        this.optimoveIntegration &&
        !!this.optimoveIntegration.params.scheduled_channel
      )
    },
    hasScheduledWebChannel() {
      return (
        this.optimoveIntegration &&
        !!this.optimoveIntegration.params.scheduled_web_channel
      )
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations`,
        params: {
          type: Integrations.optimove.type,
          active: 1
        }
      })
    },
    timezonesFormatted() {
      let timezones = this.timezones
      timezones[''] = this.$t('CampaignForm_Common_ProjectTimezone')

      return timezones
    }
  }
}
</script>

<style scoped lang="scss">
>>> .el-radio {
  height: unset !important;
  min-height: 80px !important;
  max-height: 120px !important;
  white-space: unset;
}

>>> .title {
  font-weight: 600;
  line-height: 22px;
  color: #5c6374;
}
.title-large {
  font-size: 14px;
}
.date-time-ct {
  display: flex;
}

.date-input {
  width: 200px;
}

.time-input {
  width: 121px;
}

.timezone-select {
  width: 375px;
}
.input {
  margin-right: 16px;
}
/deep/ .el-form-item__label {
  color: #5c6374;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
}
/deep/ .light-label .el-form-item__label {
  font-weight: 400;
}
.separator {
  margin: 24px -20px;
  border-top: 1px solid #ebeef5;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/schedule/ScheduleSelectorOptimove.vue